import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crosshair, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Lock, Unlock } from "lucide-react";

const PARTY_NAME = { D: "Democrats", R: "Republicans", I: "Independents" };

const partyName = (letter, legislator) => {
  if (PARTY_NAME[letter]) return PARTY_NAME[letter];
  const p = (legislator?.party || "").toLowerCase();
  return p.startsWith("dem") ? "Democrats" : p.startsWith("rep") ? "Republicans" : "their party";
};

const loyaltyTone = (rate) => {
  if (rate == null) return { pill: "bg-gray-100 text-gray-600 border-gray-200", label: "n/a" };
  if (rate >= 95) return { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Locked in" };
  if (rate >= 85) return { pill: "bg-amber-50 text-amber-700 border-amber-200", label: "Mostly loyal" };
  return { pill: "bg-orange-500/15 text-orange-200 border-orange-500/40", label: "Movable" };
};

const Trend = ({ t }) => {
  if (t.trend === "toward") return (
    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold" title={`Support warmed from ${t.earlySupport}% Yea early in the session to ${t.lateSupport}% recently`} data-testid="pp-trend-toward">
      <TrendingUp className="w-3.5 h-3.5" /> Warming
    </span>
  );
  if (t.trend === "away") return (
    <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold" title={`Support cooled from ${t.earlySupport}% Yea early in the session to ${t.lateSupport}% recently`} data-testid="pp-trend-away">
      <TrendingDown className="w-3.5 h-3.5" /> Cooling
    </span>
  );
  return <span className="text-xs text-gray-400" title="No meaningful change in support across the session">Steady</span>;
};

const LeanBars = ({ t, party }) => (
  <div className="space-y-1" title={`Votes Yea on ${t.supportRate}% of ${t.topic} roll calls; ${party} majority voted Yea ${t.partySupportRate ?? "n/a"}% of the time`}>
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-12 text-gray-500 shrink-0">Member</span>
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-blue-600" style={{ width: `${t.supportRate}%` }} />
      </div>
      <span className="w-9 text-right font-mono font-semibold text-gray-800">{t.supportRate}%</span>
    </div>
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-12 text-gray-500 shrink-0">Party</span>
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-gray-400" style={{ width: `${t.partySupportRate ?? 0}%` }} />
      </div>
      <span className="w-9 text-right font-mono text-gray-500">{t.partySupportRate ?? "n/a"}%</span>
    </div>
  </div>
);

const TopicRow = ({ t, idx, party, open, onToggle, legislatorId, saveScroll }) => {
  const tone = loyaltyTone(t.withPartyRate);
  const gap = t.partySupportRate != null ? t.supportRate - t.partySupportRate : 0;
  return (
    <div className="border border-gray-200 rounded-lg" data-testid={`pp-topic-${idx}`}>
      <button type="button" onClick={onToggle} className="w-full text-left p-3 grid grid-cols-12 gap-3 items-center hover:bg-gray-50 rounded-lg" data-testid={`pp-topic-toggle-${idx}`}>
        <div className="col-span-12 md:col-span-4 flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{t.topic}</p>
            <p className="text-[11px] text-gray-500">{t.votes} roll calls · {t.yea} Yea / {t.nay} Nay</p>
          </div>
        </div>
        <div className="col-span-12 md:col-span-4"><LeanBars t={t} party={party} /></div>
        <div className="col-span-6 md:col-span-2 flex flex-col gap-1">
          <Badge className={`border text-[11px] w-fit ${tone.pill}`} data-testid={`pp-loyalty-${idx}`}>
            {t.withPartyRate != null ? `${t.withPartyRate}% with party` : "No party line"}
          </Badge>
          {t.breaks > 0 && <span className="text-[11px] text-orange-300 font-semibold">⚡ {t.breaks} break{t.breaks === 1 ? "" : "s"}</span>}
        </div>
        <div className="col-span-6 md:col-span-2 flex flex-col items-end gap-1 text-right">
          <Trend t={t} />
          {Math.abs(gap) >= 15 && (
            <span className="text-[11px] text-gray-500">{gap > 0 ? "More supportive" : "More skeptical"} than party</span>
          )}
        </div>
      </button>
      {open && (
        <ul className="border-t border-gray-100 divide-y divide-gray-100" data-testid={`pp-topic-bills-${idx}`}>
          {t.bills.map((b) => (
            <li key={`${b.billId}-${b.date}`} className={`px-3 py-2 flex items-center gap-3 text-sm ${b.broke ? "bg-orange-500/10" : ""}`}>
              <span className="text-xs text-gray-500 w-20 shrink-0">{b.date && new Date(b.date).toLocaleDateString()}</span>
              <Link to={`/bill/${b.billId}`} state={{ fromLegislator: legislatorId }} onClick={saveScroll} className="flex-1 min-w-0 text-blue-600 hover:underline truncate" title={b.billTitle}>
                {b.billTitle}
              </Link>
              <Badge className={`border text-[11px] ${b.vote === "Yea" ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}`}>{b.vote}</Badge>
              {b.broke && <span className="text-[11px] text-orange-300 font-semibold shrink-0" title={`Party majority voted ${b.partyDirection}`}>⚡ party went {b.partyDirection}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const PressurePoints = ({ data, legislator, legislatorId, saveScroll }) => {
  const [sort, setSort] = useState("movable");
  const [open, setOpen] = useState(null);
  const topics = data?.topics || [];
  const party = partyName(data?.party, legislator);
  const lastName = (legislator?.name || "").split(" ").filter(Boolean).slice(-1)[0] || "This member";

  const sorted = useMemo(() => {
    const list = [...topics];
    if (sort === "movable") list.sort((a, b) => (a.withPartyRate ?? 101) - (b.withPartyRate ?? 101) || b.breaks - a.breaks || b.votes - a.votes);
    else list.sort((a, b) => b.votes - a.votes);
    return list;
  }, [topics, sort]);

  if (!topics.length) return null;

  const movable = (data.movable || []).map((name) => topics.find((t) => t.topic === name)).filter(Boolean);
  const warming = topics.filter((t) => t.trend === "toward");
  const cooling = topics.filter((t) => t.trend === "away");
  const totalBreaks = topics.reduce((n, t) => n + (t.breaks || 0), 0);

  const read = [
    `${lastName} votes with ${party} ${data.overallWithPartyRate ?? "n/a"}% of the time across these ${data.totalVotes} votes.`,
    movable.length
      ? `The most persuadable lanes are ${movable.map((t) => t.topic).join(", ")}, where ${lastName} has broken ranks ${movable.reduce((n, t) => n + t.breaks, 0)} time${movable.reduce((n, t) => n + t.breaks, 0) === 1 ? "" : "s"}.`
      : `No topic shows a meaningful pattern of breaking ranks; ${lastName} is a reliable party vote everywhere tracked.`,
    warming.length ? `Support is warming on ${warming.slice(0, 2).map((t) => t.topic).join(" and ")}.` : "",
    cooling.length ? `Support is cooling on ${cooling.slice(0, 2).map((t) => t.topic).join(" and ")}.` : "",
  ].filter(Boolean).join(" ");

  return (
    <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="pressure-points-card">
      <div className="flex items-center gap-2 mb-1">
        <Crosshair className="w-6 h-6 text-orange-600" />
        <h2 className="text-2xl font-bold text-gray-900">Pressure Points</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Where this member bends and where they do not, topic by topic. Built from {data.totalVotes} recorded Yea or Nay votes across the most recent roll calls this Congress, compared against how their own party voted on the same roll calls.
      </p>

      <p className="text-sm rounded-lg border border-orange-500/30 bg-orange-500/10 text-white/85 px-4 py-3 mb-5 leading-relaxed" data-testid="pp-read">{read}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-lg border border-gray-200 p-3" data-testid="pp-stat-loyalty">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">With party</p>
          <p className="text-2xl font-black text-gray-900 tabular-nums">{data.overallWithPartyRate ?? "n/a"}%</p>
          <p className="text-[11px] text-gray-500">{totalBreaks} break{totalBreaks === 1 ? "" : "s"} across topics</p>
        </div>
        <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-3" data-testid="pp-stat-movable">
          <p className="text-[11px] uppercase tracking-wider text-orange-300 font-semibold flex items-center gap-1"><Unlock className="w-3 h-3" /> Most movable</p>
          <p className="text-sm font-bold text-gray-900 leading-tight mt-1">{movable[0]?.topic || "None found"}</p>
          {movable[0] && <p className="text-[11px] text-gray-500">{movable[0].withPartyRate}% with party · {movable[0].breaks} break{movable[0].breaks === 1 ? "" : "s"}</p>}
        </div>
        <div className="rounded-lg border border-gray-200 p-3" data-testid="pp-stat-locked">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Lock className="w-3 h-3" /> Locked in</p>
          <p className="text-2xl font-black text-gray-900 tabular-nums">{(data.lockedIn || []).length}</p>
          <p className="text-[11px] text-gray-500">topics at 100% with party</p>
        </div>
        <div className="rounded-lg border border-gray-200 p-3" data-testid="pp-stat-trend">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Drifting</p>
          <p className="text-2xl font-black text-gray-900 tabular-nums">{warming.length + cooling.length}</p>
          <p className="text-[11px] text-gray-500">{warming.length} warming · {cooling.length} cooling</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500">Expand any topic to see the votes behind it. ⚡ marks a vote against the party majority.</p>
        <div className="flex gap-1 text-xs" data-testid="pp-sort">
          {[["movable", "Most movable"], ["votes", "Most votes"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setSort(k)} data-testid={`pp-sort-${k}`}
              className={`px-2.5 py-1 rounded-full border ${sort === k ? "border-orange-400 bg-orange-500/15 text-orange-200 font-semibold" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2 max-h-[34rem] overflow-y-auto pr-1" data-testid="pp-topic-list">
        {sorted.map((t, idx) => (
          <TopicRow key={t.topic} t={t} idx={idx} party={party} open={open === t.topic} onToggle={() => setOpen(open === t.topic ? null : t.topic)} legislatorId={legislatorId} saveScroll={saveScroll} />
        ))}
      </div>
    </Card>
  );
};
